import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobalStore } from '@/stores';
import {
    DeleteConfirmationModal,
    DuplicationLoadingModal,
    EditSectionModal,
    getPageTypeIcon,
    MyStoreItem,
    ProductItem,
    SectionItem,
    STORE_TABS,
    StoreItem,
    StoreProfileHeader,
    transformStoreDataToItems,
    useDeletePage,
    useDeleteSection,
    useDuplicatePage,
    useHidePage,
    useMovePage,
    useMyStore,
    useUpdatePagesOrder,
    useUpdateSectionsOrder,
    useUpdateSectionTitle,
} from '@/features/my-store';
import { handleStoreItemsReorder } from '@/features/my-store/logic/storeReorder.logic';
import { StanDraggable, StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';
import { DraggableWrapper } from '@/components/core-components/StanDraggable/DraggableWrapper';
import type { DragEvent } from '@/components/core-components/StanDraggable/StanDraggable.types';
import { useUser } from '@/queries/useUser';
import { configSettings, stanNotify } from '@/lib';
import { copyToClipboard } from '@/lib/helpers';
import { useModal } from '@/hooks/useModal';
import { StanButton } from '@/components/core-components/StanButton/StanButton';

export default function StoresPage() {
    const navigate = useNavigate();
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || STORE_TABS.DETAILS;
    const [storeItems, setStoreItems] = useState<StoreItem[]>([]);
    const [storeId, setStoreId] = useState<number>(0);
    const [itemToDelete, setItemToDelete] = useState<StoreItem | null>(null);
    const [sectionToEdit, setSectionToEdit] = useState<SectionItem | null>(null);

    // Fetch store data
    const { data: storeResponse, isLoading, error } = useMyStore();

    // Get mutations
    const updatePagesOrder = useUpdatePagesOrder(storeId);
    const updateSectionsOrder = useUpdateSectionsOrder(storeId);
    const movePage = useMovePage(storeId);
    const hidePage = useHidePage(storeId);
    const deletePage = useDeletePage(storeId);
    const updateSectionTitle = useUpdateSectionTitle(storeId);
    const deleteSection = useDeleteSection(storeId);
    const duplicatePage = useDuplicatePage(storeId);

    // Get user data for username
    const { user } = useUser();

    // Delete confirmation modal
    const deleteModal = useModal();

    // Edit section modal
    const editSectionModal = useModal();

    useEffect(() => {
        setBreadcrumbs([{ title: 'My Store', to: '/stores', active: true, id: 'linksites' }]);
    }, [setBreadcrumbs]);

    const handleTabChange = (tab: string) => {
        setSearchParams({ tab });
    };

    // Transform API data into UI items
    useEffect(() => {
        if (storeResponse && storeResponse.length > 0) {
            // Get the linksite store (not funnel)
            const linksite = storeResponse.find(store => store.type === 'linksite');
            if (linksite) {
                setStoreId(linksite.store_id);
                const items = transformStoreDataToItems(linksite);
                setStoreItems(items);
            }
        }
    }, [storeResponse]);

    // Filter items based on active tab
    const filteredItems = storeItems.filter(item => {
        // Sections only appear in details tab
        if (item.type === 'section') {
            return activeTab === STORE_TABS.DETAILS;
        }
        // Products filtered by location
        if (item.type === 'product') {
            const product = item as ProductItem;
            if (activeTab === STORE_TABS.DETAILS) {
                // Details tab shows store location products
                return product.location === 'store';
            } else if (activeTab === STORE_TABS.LANDING_PAGES) {
                // Landing pages tab shows landing location products
                return product.location === 'landing';
            }
        }
        return false;
    });

    // Handle reorder from drag and drop
    const handleReorder = (event: DragEvent) => {
        if (!storeId) return;

        const { updatedItems, payload } = handleStoreItemsReorder(event, storeItems, filteredItems);

        // Update local state immediately
        setStoreItems(updatedItems);

        // Save to backend
        if (payload.pages.length > 0) {
            updatePagesOrder.mutate(payload.pages);
        }

        if (payload.sections.length > 0) {
            updateSectionsOrder.mutate(payload.sections);
        }
    };

    if (isLoading) {
        return (
            <div className='flex w-full flex-col gap-8'>
                <div className='flex items-center justify-between'>
                    <div>
                        <h1 className='text-dark text-3xl font-bold'>My Store</h1>
                        <p className='text-secondary mt-2'>Loading your store...</p>
                    </div>
                </div>
                <div className='flex items-center justify-center py-12'>
                    <div className='border-brand-main size-8 animate-spin rounded-full border-b-2' />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='flex w-full flex-col gap-8'>
                <div className='flex items-center justify-between'>
                    <div>
                        <h1 className='text-dark text-3xl font-bold'>My Store</h1>
                        <p className='text-negative mt-2'>Failed to load store data</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!storeItems.length) {
        return (
            <div className='flex w-full flex-col gap-8'>
                <div className='flex items-center justify-between'>
                    <div>
                        <h1 className='text-dark text-3xl font-bold'>My Store</h1>
                        <p className='text-secondary mt-2'>No items in your store yet</p>
                    </div>
                </div>
            </div>
        );
    }

    // Action handlers
    const handleCopyUrl = async (item: ProductItem) => {
        if (!item.slug || !user?.username) {
            stanNotify({
                type: 'error',
                title: 'This product is not published yet!',
                text: 'Please publish the product to copy the URL.',
            });
            return;
        }

        const frontDomain = configSettings.frontDomain || 'https://stan.store/';
        const url = `${frontDomain}${user.username}/p/${item.slug}`;

        await copyToClipboard(url, {
            successMessage: 'URL copied!',
            errorMessage: 'Failed to copy URL',
        });
    };

    const handleEdit = (item: StoreItem) => {
        if (!storeId) return;

        // Handle section edit
        if (item.type === 'section') {
            setSectionToEdit(item);
            editSectionModal.open();
            return;
        }

        // Handle product edit - redirect to legacy
        // // Map page type for legacy URL (meeting is called webinar in legacy)
        // const legacyPageType = item.page_type === 'meeting' ? 'webinar' : item.page_type;
        // const legacyUrl = `${configSettings.legacyAdminDomain}stores/${storeId}/page/${legacyPageType}/${item.page_id}?skipModal=true`;
        // window.location.href = legacyUrl;

        const legacyPageType = item.page_type === 'meeting' ? 'webinar' : item.page_type;
        const url = `/stores/${storeId}/page/${legacyPageType}/${item.page_id}?skipModal=true`;
        window.location.href = url;
    };

    const handleSectionTitleUpdate = (sectionId: number, newTitle: string) => {
        updateSectionTitle.mutate({ sectionId, title: newTitle });
        editSectionModal.close();
        setSectionToEdit(null);
    };

    const handleUnpublish = (item: ProductItem) => {
        if (!storeId) return;
        hidePage.mutate(item.page_id);
    };

    const handleDuplicate = (item: StoreItem) => {
        if (!storeId) return;

        // Only products/pages can be duplicated (not sections)
        if (item.type === 'product') {
            duplicatePage.mutate(item.page_id);
        }
    };

    const handleMoveToLanding = (item: ProductItem) => {
        if (!storeId) return;
        movePage.mutate({
            pageId: item.page_id,
            moveTo: 'landing',
        });
    };

    const handleMoveToStore = (item: ProductItem) => {
        if (!storeId) return;
        movePage.mutate({
            pageId: item.page_id,
            moveTo: 'store',
        });
    };

    const handleAnalytics = (item: ProductItem) => {
        navigate(`/analytics/course/${item.page_id}`);
    };

    const handleDelete = (item: StoreItem) => {
        setItemToDelete(item);
        deleteModal.open();
    };

    const confirmDelete = () => {
        if (!storeId || !itemToDelete) return;

        if (itemToDelete.type === 'product') {
            deletePage.mutate(itemToDelete.page_id);
        } else {
            deleteSection.mutate(itemToDelete.section_id);
        }

        deleteModal.close();
        setItemToDelete(null);
    };

    // Render content for each tab
    const renderTabContent = () => {
        if (filteredItems.length === 0) {
            return (
                <div className='flex flex-col items-center justify-center py-12'>
                    <p className='text-secondary'>No items to display in this tab</p>
                </div>
            );
        }

        return (
            <>
                <StanDraggable onReorder={handleReorder} direction='vertical' gap='sm'>
                    {filteredItems.map(item => (
                        <DraggableWrapper key={item.id} id={item.id}>
                            {item.type === 'section' ? (
                                <MyStoreItem
                                    variant='section'
                                    title={item.title}
                                    item={item}
                                    onEdit={() => handleEdit(item)}
                                    onDelete={() => handleDelete(item)}
                                />
                            ) : (
                                <MyStoreItem
                                    variant='product'
                                    title={item.title}
                                    iconUrl={item.iconUrl}
                                    price={item.price}
                                    status={item.status}
                                    badgeIcon={
                                        <span className='size-4'>
                                            {(() => {
                                                const Icon = getPageTypeIcon(item.page_type);
                                                return <Icon />;
                                            })()}
                                        </span>
                                    }
                                    item={item}
                                    onCopyUrl={() => handleCopyUrl(item)}
                                    onEdit={() => handleEdit(item)}
                                    onUnpublish={() => handleUnpublish(item)}
                                    onDuplicate={() => handleDuplicate(item)}
                                    onMoveToLanding={() => handleMoveToLanding(item)}
                                    onMoveToStore={() => handleMoveToStore(item)}
                                    onAnalytics={() => handleAnalytics(item)}
                                    onDelete={() => handleDelete(item)}
                                />
                            )}
                        </DraggableWrapper>
                    ))}
                </StanDraggable>
                <StanButton name='addNewPage' onClick={() => navigate(`/stores/${storeId}/page/create`)}>
                    Add New Page
                </StanButton>
            </>
        );
    };

    return (
        <div className='flex w-full flex-col gap-8'>
            {/* Tabs */}
            <StanTabs value={activeTab} onValueChange={handleTabChange}>
                <StanTabList className='mb-6'>
                    <StanTab value={STORE_TABS.DETAILS}>Store</StanTab>
                    <StanTab value={STORE_TABS.LANDING_PAGES}>Landing Pages</StanTab>
                    <StanTab value={STORE_TABS.DESIGN}>Edit Design</StanTab>
                </StanTabList>

                <StoreProfileHeader />

                <StanTabContent value={STORE_TABS.DETAILS}>
                    <div className='flex flex-col gap-4'>{renderTabContent()}</div>
                </StanTabContent>
                <StanTabContent value={STORE_TABS.LANDING_PAGES}>
                    <div className='flex flex-col gap-4'>{renderTabContent()}</div>
                </StanTabContent>
                <StanTabContent value={STORE_TABS.DESIGN}>
                    <div className='flex flex-col gap-4'>
                        <div>
                            <h2 className='text-dark mb-2 text-xl font-semibold'>Edit Design</h2>
                            <p className='text-secondary text-sm'>Customize your store appearance</p>
                        </div>
                        <div className='bg-neutral-subtle text-secondary flex items-center justify-center rounded-lg py-12'>
                            Design customization coming soon...
                        </div>
                    </div>
                </StanTabContent>
            </StanTabs>
            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                itemTitle={itemToDelete?.title}
                itemType={itemToDelete?.type}
                isDeleting={deletePage.isPending || deleteSection.isPending}
                onConfirm={confirmDelete}
                onCancel={deleteModal.close}
            />
            {/* Duplication Loading Modal */}
            <DuplicationLoadingModal isOpen={duplicatePage.isPending} />
            {/* Edit Section Modal */}
            <EditSectionModal
                isOpen={editSectionModal.isOpen}
                section={sectionToEdit}
                isLoading={updateSectionTitle.isPending}
                onConfirm={handleSectionTitleUpdate}
                onCancel={() => {
                    editSectionModal.close();
                    setSectionToEdit(null);
                }}
            />
        </div>
    );
}
